/**
 * require-variant-choice.js
 *
 * Forces the shopper to explicitly pick certain product options before they can
 * add to cart. On the Homediq PDP the unselected state is rendered server-side
 * (see homediq-product-main.liquid), so there is no flash of a pre-selected
 * value. This script only:
 *   1. clears the pending flag once the shopper picks a value
 *   2. shows the inline error when they try to buy too early
 *   3. does the same for Horizon's own <variant-picker> (quick add), where the
 *      selection has to be cleared at runtime
 *
 * The add-to-cart button itself is made pointer-events:none while pending, so
 * Horizon's delegated on:click handler never runs and no cart animation fires.
 *
 * Plain script on purpose - no theme module imports, so load order is irrelevant.
 */
(function () {
  'use strict';

  var GROUP = '[data-require-choice="true"]';
  var BUY_AREA = '.homediq-product-main__purchase-row, .homediq-product-main__patient-form';
  var BUTTONS = '.add-to-cart-button, [data-add-to-cart-btn], [data-add-to-cart-proxy], [ref="addToCartButton"], .sticky-add-to-cart__button';
  var ERROR_DURATION = 8000;

  var timers = new WeakMap();

  function scopeOf(element) {
    return (
      element.closest('quick-add-dialog') ||
      element.closest('homediq-product-main') ||
      element.closest('.shopify-section') ||
      document
    );
  }

  function groupsIn(scope) {
    return Array.prototype.filter.call(scope.querySelectorAll(GROUP), function (group) {
      return !group.closest('product-card');
    });
  }

  function isPending(scope) {
    return groupsIn(scope).some(function (group) {
      return group.dataset.choicePending === 'true';
    });
  }

  function syncScope(element) {
    var scope = scopeOf(element);
    if (scope === document) return;
    if (isPending(scope)) {
      scope.setAttribute('data-variant-choice-pending', 'true');
    } else {
      scope.removeAttribute('data-variant-choice-pending');
    }
  }

  function showError(scope) {
    var pending = groupsIn(scope).filter(function (group) {
      return group.dataset.choicePending === 'true';
    });
    if (!pending.length) return;

    pending.forEach(function (group) {
      group.classList.add('is-choice-error');
      var message = group.querySelector('[data-variant-choice-error]');
      if (message) message.classList.remove('hidden');

      clearTimeout(timers.get(group));
      timers.set(
        group,
        setTimeout(function () {
          group.classList.remove('is-choice-error');
          if (message) message.classList.add('hidden');
        }, ERROR_DURATION)
      );
    });

    pending[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  function resolve(group) {
    group.dataset.choicePending = 'false';
    group.classList.remove('is-choice-error');
    clearTimeout(timers.get(group));

    var message = group.querySelector('[data-variant-choice-error]');
    if (message) message.classList.add('hidden');

    syncScope(group);
  }

  /** Horizon quick-add picker: clear the pre-selected radio at runtime. */
  function clearHorizonGroup(group) {
    if (group.dataset.choiceCleared === 'true') return;
    var radios = group.querySelectorAll('input[type="radio"]');
    if (!radios.length) return;

    Array.prototype.forEach.call(radios, function (radio) {
      radio.checked = false;
      radio.setAttribute('data-current-checked', 'false');
      radio.setAttribute('data-previous-checked', 'false');
    });

    group.dataset.choiceCleared = 'true';
    group.dataset.choicePending = 'true';
    syncScope(group);
  }

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    // Picking a value resolves that group.
    var button = target.closest('[data-variant-value]');
    if (button) {
      var group = button.closest(GROUP);
      if (group) resolve(group);
      return;
    }

    // The buy button is pointer-events:none while pending, so the click lands
    // on the surrounding row - that's our cue to show the error.
    var area = target.closest(BUY_AREA);
    if (area) {
      var scope = scopeOf(area);
      if (isPending(scope)) showError(scope);
    }
  });

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;
    var group = target.closest(GROUP);
    if (group) resolve(group);
  });

  // Backstop for any buy button not covered by the CSS above (quick add, sticky bar).
  function block(event) {
    var target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    var control = event.type === 'submit' ? target : target.closest(BUTTONS);
    if (!control) return;

    var scope = scopeOf(control);
    if (!isPending(scope)) return;

    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    showError(scope);
  }

  document.addEventListener('click', block, true);
  document.addEventListener('submit', block, true);

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll(GROUP), function (group) {
      if (group.closest('product-card')) return;
      clearHorizonGroup(group);
      syncScope(group);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  var scheduled = false;
  new MutationObserver(function () {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      init();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();